import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { config } from '../../utils/config';
import ClubbieLoader from '../../components/Loader/ClubbieLoader';

const FundraisingDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [donations, setDonations] = useState([]);
  const [filteredDonations, setFilteredDonations] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'Recent first'
  });

  useEffect(() => {
    fetchCampaignDetails();
  }, [id]);

  useEffect(() => {
    applyFilters();
  }, [donations, filters]);

  const fetchCampaignDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/fundraising/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch campaign');

      const data = await response.json();
      setCampaign(data);

      await fetchDonations();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchDonations = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/fundraising/${id}/donations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch donations');

      const data = await response.json();
      setDonations(data.results || []);
    } catch (error) {
    }
  };

  const applyFilters = () => {
    let filtered = [...donations];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(d =>
        d.donor?.profile?.firstName?.toLowerCase().includes(search) ||
        d.donor?.profile?.lastName?.toLowerCase().includes(search)
      );
    }

    if (filters.sortBy === 'Recent first') {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (filters.sortBy === 'Oldest first') {
      filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (filters.sortBy === 'Highest amount') {
      filtered.sort((a, b) => b.amount - a.amount);
    } else if (filters.sortBy === 'Lowest amount') {
      filtered.sort((a, b) => a.amount - b.amount);
    }

    setFilteredDonations(filtered);
  };

  const calculateProgress = () => {
    if (!campaign) return 0;
    return Math.min((campaign.raised / campaign.goal) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000F1F] flex items-center justify-center">
        <ClubbieLoader />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Campaign not found</h2>
          <button
            onClick={() => navigate('/fundraising')}
            className="text-[#00E7FF] hover:text-[#00d4e6] transition-colors"
          >
            ← Back to Fundraising
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB]">
      <div className="max-w-[1400px] mx-auto py-6 px-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/fundraising')}
            className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
          >
            ← Back to Fundraising
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#011224] rounded-2xl border border-[#192634] overflow-hidden">
              {campaign.image && (
                <div
                  className="h-80 bg-cover bg-center"
                  style={{ backgroundImage: `url(${campaign.image})` }}
                />
              )}

              <div className="p-6">
                <h1 className="text-3xl font-bold text-[#FEFCEB] mb-2">{campaign.title}</h1>
                <p className="text-[#7F8585] mb-6">{campaign.subtitle}</p>

                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="bg-[#082441] rounded-xl border border-[#192634] p-6">
                    <span className="block text-[#AEB9E1] text-sm mb-2">Raised</span>
                    <span className="text-[#FEFCEB] text-3xl font-semibold">£{campaign.raised?.toLocaleString() || 0}</span>
                  </div>
                  <div className="bg-[#082441] rounded-xl border border-[#192634] p-6">
                    <span className="block text-[#AEB9E1] text-sm mb-2">Goal</span>
                    <span className="text-[#FEFCEB] text-3xl font-semibold">£{campaign.goal?.toLocaleString() || 0}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-[#FEFCEB] font-semibold">
                      {calculateProgress().toFixed(0)}% funded
                    </span>
                    <span className="text-[#7F8585]">
                      {donations.length} donors
                    </span>
                  </div>
                  <div className="h-3 bg-[#192634] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#00E7FF] transition-all"
                      style={{ width: `${calculateProgress()}%` }}
                    />
                  </div>
                </div>

                {campaign.description && (
                  <div className="border-t border-[#192634] pt-6">
                    <h3 className="text-lg font-semibold text-[#FEFCEB] mb-3">Campaign details</h3>
                    <p className="text-[#AEB9E1] whitespace-pre-wrap">{campaign.description}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#FEFCEB]">Donations</h3>
                <div className="flex items-center gap-4">
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Search donors..."
                    className="bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2 border border-[#192634] outline-none focus:border-[#00E7FF] placeholder-[#7F8585]"
                  />
                  <select
                    value={filters.sortBy}
                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                    className="bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2 border border-[#192634] outline-none focus:border-[#00E7FF]"
                  >
                    <option value="Recent first">Recent first</option>
                    <option value="Oldest first">Oldest first</option>
                    <option value="Highest amount">Highest amount</option>
                    <option value="Lowest amount">Lowest amount</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#192634]">
                      <th className="text-left py-3 px-4 text-[#AEB9E1] text-sm font-medium">Donor</th>
                      <th className="text-left py-3 px-4 text-[#AEB9E1] text-sm font-medium">Team</th>
                      <th className="text-left py-3 px-4 text-[#AEB9E1] text-sm font-medium">Amount</th>
                      <th className="text-left py-3 px-4 text-[#AEB9E1] text-sm font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDonations.map((donation) => (
                      <tr key={donation.id} className="border-b border-[#192634] hover:bg-[#082441] transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            {donation.donor?.profile?.avatar ? (
                              <div
                                className="w-8 h-8 rounded-full bg-cover bg-center"
                                style={{ backgroundImage: `url(${donation.donor.profile.avatar})` }}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#082441] flex items-center justify-center text-[#FEFCEB] text-xs font-semibold">
                                {donation.donor?.profile?.firstName?.[0]}{donation.donor?.profile?.lastName?.[0]}
                              </div>
                            )}
                            <span className="text-[#FEFCEB]">
                              {donation.donor?.profile?.firstName} {donation.donor?.profile?.lastName}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            {donation.team?.crest && (
                              <div
                                className="w-6 h-6 rounded-full bg-cover bg-center"
                                style={{ backgroundImage: `url(${donation.team.crest})` }}
                              />
                            )}
                            <span className="text-[#AEB9E1]">{donation.team?.title || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-[#FEFCEB] font-medium">£{donation.amount.toFixed(2)}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-[#AEB9E1]">
                            {new Date(donation.createdAt).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredDonations.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-[#7F8585]">No donations yet</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#192634]">
                <span className="text-[#7F8585] text-sm">
                  Showing {filteredDonations.length} of {donations.length} donations
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-[#FEFCEB] mb-4">Support this campaign</h3>

              {campaign.donationOptions && campaign.donationOptions.length > 0 && (
                <div className="space-y-3 mb-6">
                  {campaign.donationOptions.map((option, index) => (
                    <button
                      key={index}
                      className="w-full bg-[#082441] hover:bg-[#0a2f5c] text-left p-4 rounded-lg border border-[#192634] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[#FEFCEB] font-medium">{option.label}</span>
                        <span className="text-[#00E7FF] font-bold">£{option.amount}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {campaign.allowCustomDonation && (
                <button className="w-full bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-6 py-3 rounded-full font-semibold transition-colors">
                  Make a donation
                </button>
              )}

              <div className="mt-6 pt-6 border-t border-[#192634] space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#7F8585]">Start date:</span>
                  <span className="text-[#FEFCEB]">
                    {new Date(campaign.startDate).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#7F8585]">End date:</span>
                  <span className="text-[#FEFCEB]">
                    {new Date(campaign.endDate).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#7F8585]">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${campaign.status === 'active'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                    }`}>
                    {campaign.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundraisingDetails;
